import { useState } from "react";
import { Reveal, Eyebrow } from "./Reveal";
import { MapPin, Phone, Mail, Send, ReceiptText } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPI = [
  ["Ristrutturazione", "Renovation"],
  ["Nuova costruzione", "New build"],
  ["Impianti", "Systems"],
  ["Lavori chiavi in mano", "Turnkey project"],
  ["Altro", "Other"],
];

export const Contatti = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", tipo: TIPI[0][0], messaggio: "" });
  const [sending, setSending] = useState(false);

  const CONTACTS = [
    {
      icon: MapPin,
      label: t("Sede operativa", "Head office"),
      value: "Via della Beverara 129, 40131 Bologna (BO)",
      href: "https://www.google.com/maps/search/?api=1&query=Via+della+Beverara+129+40131+Bologna",
      testid: "contact-address-link",
    },
    {
      icon: Phone,
      label: t("Telefono", "Phone"),
      value: "+39 351 466 5019",
      href: "tel:+393514665019",
      testid: "contact-phone-link",
    },
    {
      icon: Mail,
      label: "Email",
      value: "adriaticasrlbologna@gmail.com",
      href: "mailto:adriaticasrlbologna@gmail.com",
      testid: "contact-email-link",
    },
  ];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/quotes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error();
      toast.success(t("Richiesta inviata! Ti ricontatteremo entro 24 ore lavorative.", "Request sent! We'll get back to you within 24 business hours."));
      setForm({ nome: "", email: "", telefono: "", tipo: TIPI[0][0], messaggio: "" });
    } catch {
      toast.error(t("Invio non riuscito. Riprova o chiamaci al +39 351 466 5019.", "Sending failed. Please try again or call us at +39 351 466 5019."));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-[#0E1726] border border-[#D4AF37]/25 rounded-xl px-4 py-3 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/50 transition-colors";

  return (
    <section id="contatti" data-testid="contatti-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 blueprint-dots opacity-[0.05] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="max-w-2xl">
          <Eyebrow>{t("Contatti", "Contact")}</Eyebrow>
          <h2 className="mt-5 font-display font-semibold text-white text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
            {t("Costruiamo insieme", "Let's build together")}{" "}
            <span className="font-serif italic font-medium text-gold-gradient">{t("il tuo progetto", "your project")}</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5 flex flex-col gap-5">
            {CONTACTS.map((c, i) => (
              <Reveal key={c.testid} delay={0.08 * i}>
                <a
                  href={c.href}
                  data-testid={c.testid}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="trace-card group flex items-center gap-5 rounded-2xl bg-[#0E1726] p-6 hover:-translate-y-1"
                >
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#D4AF37]/12 border border-[#D4AF37]/40 shrink-0 transition-transform duration-300 group-hover:scale-110">
                    <c.icon size={20} className="text-[#D4AF37]" />
                  </span>
                  <span>
                    <span className="block text-[11px] uppercase tracking-[0.25em] text-slate-500">{c.label}</span>
                    <span className="block mt-1 text-sm sm:text-base text-slate-200 group-hover:text-[#F3E08B] transition-colors">
                      {c.value}
                    </span>
                  </span>
                </a>
              </Reveal>
            ))}
            <Reveal delay={0.24}>
              <div data-testid="contact-company-data" className="trace-card rounded-2xl bg-[#0E1726] p-6">
                <span className="block text-[11px] uppercase tracking-[0.25em] text-slate-500">{t("Dati societari", "Company details")}</span>
                <span className="block mt-1 text-sm sm:text-base text-slate-200">Adriatica S.r.l. Semplificata</span>
                <span className="block mt-0.5 text-sm text-slate-400">{t("P.IVA", "VAT no.")} 03525571208</span>
              </div>
            </Reveal>
            <Reveal delay={0.28}>
              <div data-testid="detrazioni-callout" className="rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-r from-[#D4AF37]/15 to-transparent p-6 flex gap-4">
                <ReceiptText className="text-[#D4AF37] shrink-0 mt-0.5" size={22} />
                <p className="text-sm sm:text-base text-slate-200 leading-relaxed">
                  <span className="font-semibold text-[#F3E08B]">{t("Detrazioni fiscali.", "Tax deductions.")}</span>{" "}
                  {t(
                    "Su molti lavori di ristrutturazione e riqualificazione puoi usufruire delle detrazioni fiscali previste dalla normativa vigente: ti affianchiamo nella gestione della documentazione.",
                    "Many renovation and upgrade works qualify for the tax deductions provided by current regulations: we support you with the paperwork."
                  )}
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="text-sm text-slate-500 leading-relaxed px-1">
                {t(
                  "Sopralluogo e preventivo gratuiti a Bologna e provincia. Ti ricontattiamo entro 24 ore lavorative.",
                  "Free site visit and quote in Bologna and its province. We'll get back to you within 24 business hours."
                )}
              </p>
            </Reveal>
          </div>

          <Reveal className="lg:col-span-7" delay={0.15}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="trace-card rounded-2xl bg-[#0E1726]/80 backdrop-blur-xl p-7 sm:p-9"
            >
              <h3 className="font-serif text-2xl font-medium text-white">{t("Richiedi un preventivo", "Request a quote")}</h3>
              <p className="mt-2 text-sm text-slate-400">
                {t("Compila il modulo: ti ricontatteremo entro 24 ore lavorative.", "Fill in the form: we'll get back to you within 24 business hours.")}
              </p>

              <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="cf-nome" className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">{t("Nome e cognome", "Full name")}</label>
                  <input id="cf-nome" data-testid="contact-form-name-input" required value={form.nome} onChange={set("nome")} placeholder={t("Mario Rossi", "John Smith")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-email" className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Email</label>
                  <input id="cf-email" data-testid="contact-form-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="mario@esempio.it" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-tel" className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">{t("Telefono", "Phone")}</label>
                  <input id="cf-tel" data-testid="contact-form-phone-input" value={form.telefono} onChange={set("telefono")} placeholder="+39 ..." className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-tipo" className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">{t("Tipo di lavoro", "Type of work")}</label>
                  <select id="cf-tipo" data-testid="contact-form-type-select" value={form.tipo} onChange={set("tipo")} className={inputCls}>
                    {TIPI.map(([it, en]) => (
                      <option key={it} value={it}>{lang === "en" ? en : it}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-msg" className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">{t("Messaggio", "Message")}</label>
                  <textarea id="cf-msg" data-testid="contact-form-message-input" required rows={4} value={form.messaggio} onChange={set("messaggio")} placeholder={t("Descrivi brevemente il tuo progetto...", "Briefly describe your project...")} className={`${inputCls} resize-none`} />
                </div>
              </div>

              <button
                type="submit"
                data-testid="contact-form-submit-button"
                disabled={sending}
                className="group mt-7 inline-flex items-center gap-2 text-sm sm:text-base font-semibold text-[#070D18] bg-gradient-to-r from-[#D4AF37] to-[#F3E08B] px-7 py-3.5 rounded-full hover:shadow-[0_0_36px_rgba(212,175,55,0.5)] transition-shadow duration-300 disabled:opacity-60"
              >
                {sending ? t("Invio in corso…", "Sending…") : t("Invia la richiesta", "Send request")}
                <Send size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>

        <Reveal className="mt-14" delay={0.1}>
          <div className="trace-card rounded-2xl overflow-hidden bg-[#0E1726]" data-testid="contact-map">
            <iframe
              title={t("Mappa della sede Adriatica — Via della Beverara 129, Bologna", "Map of the Adriatica office — Via della Beverara 129, Bologna")}
              src="https://maps.google.com/maps?q=Via+della+Beverara+129%2C+40131+Bologna&z=15&output=embed"
              className="w-full h-80 sm:h-96 border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
